import React, { useState } from 'react';
import { Upload, Loader } from 'lucide-react';
import { sendQuoteRequest, validateQuoteForm } from '../utils/emailService';

const ContactSection = ({ sectionRef, translations, language }) => {
  const t = translations;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    size: '',
    cakeType: '',
    referenceImage: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validation = validateQuoteForm(formData, language);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    
    setIsSubmitting(true);
    setFormErrors({});
    setSubmitMessage('');
    
    try {
      // Send email
      const result = await sendQuoteRequest(formData, language);
      
      if (result.success) {
        setSubmitMessage(result.message);
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          phone: '',
          description: '',
          size: '',
          cakeType: '',
          referenceImage: null
        });
      } else {
        setSubmitMessage(result.message);
      }
    } catch (error) {
      setSubmitMessage(
        language === 'en' 
          ? 'An unexpected error occurred. Please try again.' 
          : 'A apărut o eroare neașteptată. Încercați din nou.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors({
          ...formErrors,
          referenceImage: language === 'en' 
            ? 'File size must be less than 5MB' 
            : 'Mărimea fișierului trebuie să fie mai mică de 5MB'
        });
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setFormErrors({
          ...formErrors,
          referenceImage: language === 'en' 
            ? 'Please upload an image file' 
            : 'Vă rugăm să încărcați un fișier imagine'
        });
        return;
      }
      
      setFormData({ ...formData, referenceImage: file });
      // Clear any previous errors for this field
      const newErrors = { ...formErrors };
      delete newErrors.referenceImage;
      setFormErrors(newErrors);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      const newErrors = { ...formErrors };
      delete newErrors[field];
      setFormErrors(newErrors);
    }
  };

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t.contactTitle}
          </h2>
          <p className="text-xl text-gray-600">{t.contactSubtitle}</p>
        </div>

        <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 shadow-xl">
          {/* Size Reference Guide */}
          <div className="bg-white rounded-2xl p-6 mb-8 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
              Cake Size Guide (Weight & Servings)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center p-3 bg-pink-50 rounded-lg">
                <div className="font-semibold text-pink-600">1-2kg</div>
                <div className="text-gray-600">8-12 servings</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="font-semibold text-purple-600">2-3kg</div>
                <div className="text-gray-600">15-20 servings</div>
              </div>
              <div className="text-center p-3 bg-indigo-50 rounded-lg">
                <div className="font-semibold text-indigo-600">3-4kg</div>
                <div className="text-gray-600">25-30 servings</div>
              </div>
              <div className="text-center p-3 bg-rose-50 rounded-lg">
                <div className="font-semibold text-rose-600">4-5kg+</div>
                <div className="text-gray-600">35+ servings</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleFormSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t.formName} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300 ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t.formEmail} *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300 ${
                    formErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  {t.formPhone}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300 ${
                    formErrors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.phone && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Cake Type
                </label>
                <select
                  value={formData.cakeType}
                  onChange={(e) => handleInputChange('cakeType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="">
                    {language === 'en' ? 'Select cake type...' : 'Selectați tipul tortului...'}
                  </option>
                  <option value="birthday">
                    {language === 'en' ? 'Birthday Cake' : 'Tort de zi de naștere'}
                  </option>
                  <option value="wedding">
                    {language === 'en' ? 'Wedding Cake' : 'Tort de nuntă'}
                  </option>
                  <option value="anniversary">
                    {language === 'en' ? 'Anniversary Cake' : 'Tort de aniversare'}
                  </option>
                  <option value="custom">
                    {language === 'en' ? 'Custom Design' : 'Design personalizat'}
                  </option>
                  <option value="cupcakes">
                    {language === 'en' ? 'Cupcakes' : 'Cupcakes'}
                  </option>
                  <option value="other">
                    {language === 'en' ? 'Other' : 'Altele'}
                  </option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Cake Weight & Servings *
              </label>
              <select
                required
                value={formData.size}
                onChange={(e) => handleInputChange('size', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300 ${
                  formErrors.size ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">
                  {language === 'en' ? 'Select cake size...' : 'Selectați mărimea tortului...'}
                </option>
                <option value="1-2kg">
                  1-2kg ({language === 'en' ? '8-12 servings' : '8-12 porții'})
                </option>
                <option value="2-3kg">
                  2-3kg ({language === 'en' ? '15-20 servings' : '15-20 porții'})
                </option>
                <option value="3-4kg">
                  3-4kg ({language === 'en' ? '25-30 servings' : '25-30 porții'})
                </option>
                <option value="4-5kg">
                  4-5kg ({language === 'en' ? '35+ servings' : '35+ porții'})
                </option>
                <option value="5kg+">
                  5kg+ ({language === 'en' ? 'Large custom order' : 'Comandă mare personalizată'})
                </option>
              </select>
              {formErrors.size && (
                <p className="text-red-500 text-sm mt-1">{formErrors.size}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                {t.formDescription} *
              </label>
              <textarea
                required
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-300 ${
                  formErrors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={language === 'en' ? 'Tell us about your dream cake - flavors, colors, decorations, theme, or any special requirements...' : 'Spuneți-ne despre tortul visurilor voastre - arome, culori, decorațiuni, temă sau cerințe speciale...'}
              />
              {formErrors.description && (
                <p className="text-red-500 text-sm mt-1">{formErrors.description}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                {t.formReference}
              </label>
              <div className={`border-2 border-dashed rounded-xl p-6 text-center hover:border-pink-400 transition-colors duration-300 ${
                formErrors.referenceImage ? 'border-red-500' : 'border-gray-300'
              }`}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="imageUpload"
                />
                <label htmlFor="imageUpload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">{t.formReferencePlaceholder}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'en' ? 'Max file size: 5MB' : 'Mărime maximă: 5MB'}
                  </p>
                  {formData.referenceImage && (
                    <p className="text-pink-600 mt-2 font-medium">
                      ✓ {formData.referenceImage.name}
                    </p>
                  )}
                </label>
              </div>
              {formErrors.referenceImage && (
                <p className="text-red-500 text-sm mt-1">{formErrors.referenceImage}</p>
              )}
            </div>

            {/* Pricing Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-blue-800 text-sm">
                <strong>{language === 'en' ? 'Note:' : 'Notă:'}</strong>{' '}
                {language === 'en' 
                  ? 'All cakes are priced by weight (kg) and complexity. We\'ll provide a detailed quote within 24 hours including design mockup and pricing breakdown.'
                  : 'Toate torturile sunt prețuite în funcție de greutate (kg) și complexitate. Vă vom furniza o cotație detaliată în termen de 24 de ore, inclusiv macheta de design și defalcarea prețurilor.'
                }
              </p>
            </div>

            {/* Submit Message */}
            {submitMessage && (
              <div className={`p-4 rounded-xl text-center ${
                submitMessage.includes('success') || submitMessage.includes('succes') 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {submitMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 px-8 rounded-xl text-lg font-semibold transition-all duration-300 ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:shadow-lg transform hover:scale-105'
              } text-white`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                  {language === 'en' ? 'Sending...' : 'Se trimite...'}
                </div>
              ) : (
                t.submitQuote
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;